export type SelectionItem = {
  productId: string;
  optionId: string | null;
  price: number;
  quantity: number;
};

export const isOrderItemExist = (item: SelectionItem, productId: string, optionId: string | null) =>
  item.productId === productId && item.optionId === optionId;

export type ProductDeliveryState = {
  includeDeliveryFee: boolean;
  selectedDeliveryFeeOptionIndex: number;
};

export type SelectionState = {
  orderItems: SelectionItem[];
  deliveryByProduct: Record<string, ProductDeliveryState>;
};

type SelectionAction =
  | { type: 'TOGGLE_ITEM'; productId: string; optionId: string | null; price: number }
  | { type: 'INCREASE_QUANTITY'; productId: string; optionId: string | null }
  | { type: 'DECREASE_QUANTITY'; productId: string; optionId: string | null }
  | { type: 'TOGGLE_DELIVERY_FEE'; productId: string }
  | { type: 'SELECT_DELIVERY_FEE_OPTION'; productId: string; index: number }
  | { type: 'RESET' };

export const initialState: SelectionState = {
  orderItems: [],
  deliveryByProduct: {},
};

export const DEFAULT_DELIVERY_STATE: ProductDeliveryState = {
  includeDeliveryFee: false,
  selectedDeliveryFeeOptionIndex: 0,
};

const hasIncludeProduct = (orderItems: SelectionItem[], productId: string) =>
  orderItems.some((item) => item.productId === productId);

const removeItem = (state: SelectionState, productId: string, optionId: string | null): SelectionState => {
  const orderItems = state.orderItems.filter((item) => !isOrderItemExist(item, productId, optionId));
  const hasRemainingItems = hasIncludeProduct(orderItems, productId);

  return {
    orderItems,
    deliveryByProduct: hasRemainingItems
      ? state.deliveryByProduct
      : Object.fromEntries(Object.entries(state.deliveryByProduct).filter(([id]) => id !== productId)),
  };
};

const updateDelivery = (
  state: SelectionState,
  productId: string,
  patch: Partial<ProductDeliveryState>
): SelectionState => {
  if (!hasIncludeProduct(state.orderItems, productId)) return state;
  const current = state.deliveryByProduct[productId] ?? DEFAULT_DELIVERY_STATE;

  return {
    ...state,
    deliveryByProduct: { ...state.deliveryByProduct, [productId]: { ...current, ...patch } },
  };
};

export const chatOrderReducer = (state: SelectionState, action: SelectionAction): SelectionState => {
  switch (action.type) {
    case 'TOGGLE_ITEM': {
      const { productId, optionId, price } = action;
      const isAlreadyExists = state.orderItems.some((item) => isOrderItemExist(item, productId, optionId));

      return isAlreadyExists
        ? removeItem(state, productId, optionId)
        : { ...state, orderItems: [...state.orderItems, { productId, optionId, price, quantity: 1 }] };
    }

    case 'INCREASE_QUANTITY': {
      const { productId, optionId } = action;
      const target = state.orderItems.find((item) => isOrderItemExist(item, productId, optionId));
      if (!target) return state;

      return {
        ...state,
        orderItems: state.orderItems.map((item) =>
          isOrderItemExist(item, productId, optionId) ? { ...item, quantity: item.quantity + 1 } : item
        ),
      };
    }

    case 'DECREASE_QUANTITY': {
      const { productId, optionId } = action;
      const target = state.orderItems.find((item) => isOrderItemExist(item, productId, optionId));

      if (!target) return state;
      if (target.quantity <= 1) return removeItem(state, productId, optionId);

      return {
        ...state,
        orderItems: state.orderItems.map((item) =>
          isOrderItemExist(item, productId, optionId) ? { ...item, quantity: item.quantity - 1 } : item
        ),
      };
    }

    case 'TOGGLE_DELIVERY_FEE': {
      const current = state.deliveryByProduct[action.productId] ?? DEFAULT_DELIVERY_STATE;

      return updateDelivery(state, action.productId, { includeDeliveryFee: !current.includeDeliveryFee });
    }

    case 'SELECT_DELIVERY_FEE_OPTION':
      return updateDelivery(state, action.productId, { selectedDeliveryFeeOptionIndex: action.index });

    case 'RESET':
      return initialState;
  }
};
